import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TelegramChat from "./TelegramChat";
import ClearIcon from "@mui/icons-material/Clear";
import { userSlice } from "../redux/user/userSlice";
import { useLocation } from "react-router-dom";
import io from "socket.io-client";
import { getTelegramPreviewPlan } from "../utils/telegramPreview";
import { getAuthTokenRaw } from "../utils/tokenHeaders";
import {
  buildStartPostPreview,
  markStartPostShownInPreview,
  wasStartPostShownInPreview,
} from "../utils/startPostPreview";

const { clearMessages, addMessage } = userSlice;

const prodURL = "https://club-lab.ru";
const socketUrl =
  window.location.hostname === "localhost" ? "http://localhost:3005" : prodURL;

function createPreviewSocket() {
  return io(socketUrl, {
    path: "/socket.io",
    transports: ["websocket"],
    secure: true,
    auth: {
      token: getAuthTokenRaw(),
    },
  });
}

export default function TelegramView() {
  const dispatch = useDispatch();
  const location = useLocation();
  const socketRef = useRef(null);

  const [activeButton, setActiveButton] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [buttonsData, setButtonsData] = useState(null);
  const [localPreviewType, setLocalPreviewType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const actionStatus = useSelector((state) => state.user?.actionStatus);
  const game = useSelector((state) => state.user.game);

  const handleActive = () => {
    setActiveButton(!activeButton);
  };

  const handleLeave = () => {
    setActiveButton(activeButton);
  };

  useEffect(() => {
    if (actionStatus) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [actionStatus]);

  useEffect(() => {
    setVideoSrc(null);
    setButtonsData(null);
    setLocalPreviewType(null);
  }, [location.pathname, selectedGameId]);

  useEffect(() => {
    const socket = createPreviewSocket();
    socketRef.current = socket;

    const handleFileData = (data) => {
      const { fileData, buttons } = data;
      const blob = new Blob([fileData], { type: "video/mp4" });
      const blobUrl = URL.createObjectURL(blob);
      setIsLoading(false);
      setLocalPreviewType(null);
      setVideoSrc(blobUrl);
      setButtonsData(buttons);
    };

    const handlePreviewError = (message) => {
      setIsLoading(false);
      dispatch(
        addMessage({
          type: "help",
          message:
            typeof message === "string"
              ? message
              : "Не удалось загрузить предпросмотр сцены.",
        })
      );
    };

    socket.on("fileData", handleFileData);
    socket.on("error", handlePreviewError);
    socket.on("previewUnavailable", handlePreviewError);

    return () => {
      socket.off("fileData", handleFileData);
      socket.off("error", handlePreviewError);
      socket.off("previewUnavailable", handlePreviewError);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [dispatch]);

  const handleRequestFile = (scene = 1) => {
    const socket = socketRef.current;
    if (!socket) {
      return;
    }
    if (!selectedGameId) {
      dispatch(
        addMessage({
          type: "error",
          message:
            "Чтобы запустить сценарий, необходимо перейти в него и после этого нажать «Запустить».",
        })
      );
      return;
    }

    if (!game || String(game._id) !== String(selectedGameId)) {
      dispatch(
        addMessage({
          type: "help",
          message: "Подождите, проект ещё загружается…",
        })
      );
      return;
    }

    if (!wasStartPostShownInPreview(selectedGameId)) {
      const startPostPreview = buildStartPostPreview(game);
      if (startPostPreview) {
        dispatch(
          addMessage({
            type: "start-post",
            preview: startPostPreview,
            label: "Стартовый пост",
          })
        );
        markStartPostShownInPreview(selectedGameId);
      }
    }

    const plan = getTelegramPreviewPlan(game, scene);

    setVideoSrc(null);
    setButtonsData(null);
    setLocalPreviewType(null);

    if (plan.mode === "message") {
      setIsLoading(false);
      dispatch(
        addMessage({
          type: plan.type ?? "help",
          message: plan.message,
        })
      );
      return;
    }

    if (plan.mode === "local") {
      setIsLoading(false);
      setLocalPreviewType(plan.contentType);
      return;
    }

    setIsLoading(true);
    socket.emit("requestFile", {
      projectId: selectedGameId,
      scene: plan.scene ?? scene,
    });
  };

  return (
    <aside className="app-panel">
      <div className="app-panel__header">
        <ClearIcon onClick={() => dispatch(clearMessages())} />
      </div>

      <TelegramChat
        selectedGameId={selectedGameId}
        handleRequestFile={handleRequestFile}
        videoSrc={videoSrc}
        buttonsData={buttonsData}
        isLoading={isLoading}
        localPreviewType={localPreviewType}
        game={game}
      />

      <div
        className={`app-panel__run${activeButton ? " app-panel__run--active" : ""}`}
        onMouseDown={handleActive}
        onMouseUp={handleActive}
        onMouseLeave={handleLeave}
        onClick={() => handleRequestFile(1)}
      >
        Запустить
      </div>
    </aside>
  );
}
